// =============================================================================
// PREVIOUS IMPLEMENTATION — kept verbatim below for reference, intentionally
// inert. Line comments rather than a /* */ block: the original text contains
// comment terminators, which would close a block comment early.
//
// The active implementation follows after this block. Nothing above it is live
// code — it is here so the previous behaviour stays readable in place.
// =============================================================================

// import React, { FC } from "react";
// import StepFooter from "../StepFooter";
// import { HomeHeartIcon, PersonIcon, ShieldCheckIcon } from "../icons";
// import { PanelProps } from "./types";
// import { useLocale } from "../../i18n/locale";
//
// /**
//  * Step 4. The end of the before-arrival phase: a confirmation, plus what will
//  * be waiting once the tenant has checked in. Flow A finishes here, so the
//  * upcoming list and the CTA both disappear when there is no after-arrival
//  * phase to unlock.
//  */
// const ArrivalPanel: FC<PanelProps> = ({ steps, onBack, onContinue }) => {
//   const { t } = useLocale();
//   const hasAfterArrival = steps.some((step) => step.phase === "after");
//
//   const upcoming = [
//     { label: t.arrival.upcomingInventory, icon: <PersonIcon /> },
//     { label: t.arrival.upcomingInsurance, icon: <ShieldCheckIcon /> },
//     { label: t.arrival.upcomingPractical, icon: <ShieldCheckIcon /> },
//   ];
//
//   return (
//     <>
//       <div className="flex items-start gap-6 border border-primary-6000/20 bg-cream-100 p-7">
//         <span className="flex h-16 w-16 flex-none items-center justify-center rounded-full text-primary-6000 ring-1 ring-primary-6000">
//           <HomeHeartIcon className="h-9 w-9" />
//         </span>
//
//         <div className="min-w-0">
//           <p className="text-[13.5px] leading-relaxed text-neutral-600">
//             {t.arrival.body}
//           </p>
//
//           {hasAfterArrival && (
//             <ul className="mt-5 flex flex-col gap-3">
//               {upcoming.map(({ label, icon }) => (
//                 <li key={label} className="flex items-center gap-3">
//                   <span className="flex-none text-primary-6000">{icon}</span>
//                   <span className="text-[13px] font-medium text-navy-900">
//                     {label}
//                   </span>
//                 </li>
//               ))}
//             </ul>
//           )}
//         </div>
//       </div>
//
//       <StepFooter
//         onBack={onBack}
//         onContinue={onContinue}
//         label={t.desktop.steps.arrival.cta}
//       />
//     </>
//   );
// };
//
// export default ArrivalPanel;

// ==== ACTIVE IMPLEMENTATION ==================================================

import React, { FC, useState } from "react";
import ResultScreen from "../ResultScreen";
import { useReservation } from "../reservationContext";
import { useOnboardingRecord } from "../onboardingContext";
import { downloadSignedDocuments } from "../onboardingConfig";
import { PanelProps } from "./types";
import { useLocale } from "../../i18n/locale";
import { formatStayDate } from "../../i18n/formatDate";

/**
 * Step 4 — the end of the before-arrival flow.
 *
 * A result screen rather than another form: everything required before the keys
 * change hands has been collected, so this confirms it, says when and where to
 * turn up, and flags that two more steps open after check-in.
 */
const ArrivalPanel: FC<PanelProps> = ({ steps, state, goTo, onContinue }) => {
  const { locale, t } = useLocale();
  const reservation = useReservation();
  const onboarding = useOnboardingRecord();
  const c = t.desktop.arrivalPanel;
  const hasAfterArrival = steps.some((step) => step.phase === "after");

  const [downloading, setDownloading] = useState(false);

  // Offered only once there is something countersigned to hand over: until the
  // tenant signs, the stored file is the unsigned copy they were emailed.
  //
  // onboarding.contract_signed comes from the fetched record and is not updated
  // when the tenant signs in this same session — only the local state is. Using
  // both means the button enables straight after signing, without needing a
  // page refresh to refetch the record.
  const canDownload = !!onboarding?.contract_signed || state.documentsSigned;

  const handleDownload = async () => {
    if (!onboarding) return;
    setDownloading(true);
    await downloadSignedDocuments(
      onboarding.generated_contract_pdf_id,
      onboarding.documents[0]?.title || "Rental contract"
    );
    setDownloading(false);
  };

  // "03" and "SEPTEMBER 2026" are set separately in the banner.
  const checkIn = new Date(`${reservation.checkIn}T00:00:00Z`);
  const checkInDay = new Intl.DateTimeFormat(locale === "fr" ? "fr-FR" : "en-GB", {
    day: "2-digit",
    timeZone: "UTC",
  }).format(checkIn);
  const checkInMonth = new Intl.DateTimeFormat(
    locale === "fr" ? "fr-FR" : "en-GB",
    { month: "long", year: "numeric", timeZone: "UTC" }
  ).format(checkIn);

  const items = [
    {
      id: "identity",
      title: c.identityDone,
      caption: c.identityDoneCaption,
    },
    {
      id: "contract",
      title: c.contractDone,
      caption: c.contractDoneCaption,
    },
  ];

  // The attestation is only signed on a mobility lease.
  if (state.scenario === "contract+attestation") {
    items.push({
      id: "attestation",
      title: c.attestationDone,
      caption: c.attestationDoneCaption,
    });
  }

  return (
    <ResultScreen
      eyebrow={c.eyebrow}
      title={c.title}
      subtitle={c.subtitle(
        reservation.tenantFirstName,
        reservation.property,
        formatStayDate(reservation.checkIn, locale)
      )}
      items={items}
      downloadLabel={c.download}
      onDownload={canDownload ? handleDownload : undefined}
      downloading={downloading}
      ctaLabel={c.cta}
      // The panel's own onContinue, not a jump to a named step. Calling goTo
      // directly skipped the recording, so arrival stayed pending for ever:
      // a returning tenant was sent back to it after the inventory, and the
      // journey could never reach "completed". It also hard-coded "inventory",
      // so a reservation with only the insurance step enabled would have jumped
      // to a step that was not in its own flow.
      onContinue={hasAfterArrival ? onContinue : undefined}
      restartLabel={c.restart}
      // Design-review only; a real tenant does not restart their onboarding.
      onRestart={onboarding ? undefined : () => goTo("identity")}
    >
      <div className="mx-auto mt-5 flex max-w-[560px] items-center gap-[18px] border-l-[3px] border-gold bg-cream px-[19px] py-2 text-left">
        <span className="font-serif text-[37px] font-normal text-navy">
          {checkInDay}
        </span>
        <div>
          <small className="block text-[12px] min-[760px]:text-[12px] font-extrabold uppercase tracking-[0.13em] text-gold">
            {checkInMonth}
          </small>
          <strong className="block text-[12px] font-semibold text-navy">
            {c.checkInFrom}
          </strong>
          <p className="m-0 text-[13px] min-[760px]:text-[13px] text-[#8b9196]">{reservation.address}</p>
        </div>
      </div>

      {hasAfterArrival && (
        <div className="mx-auto mt-5 flex max-w-[560px] items-center gap-[14px] border-l-[3px] border-gold bg-cream px-4 py-[12px] text-left">
          <span className="flex-none whitespace-nowrap bg-gold px-[7px] py-[3px] text-[11px] min-[760px]:text-[11px] uppercase tracking-[0.12em] text-white">
            {c.calloutTag}
          </span>
          <div>
            <strong className="text-[13px] font-semibold text-navy">
              {c.calloutTitle}
            </strong>
            <p className="mt-1 text-[13px] min-[760px]:text-[12px] leading-[1.5] text-[#818990]">
              {c.calloutBody}
            </p>
          </div>
        </div>
      )}
    </ResultScreen>
  );
};

export default ArrivalPanel;
